import React, { useState, useEffect } from 'react';
import { Globe, Copy, RefreshCw } from 'lucide-react';
import { Button } from './Button';

interface IPInfo {
  ip: string;
  source: string;
  timestamp: string;
}

export const IPDetector: React.FC = () => {
  const [ipAddresses, setIpAddresses] = useState<IPInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const detectIPs = async () => {
    setLoading(true);
    const newIPs: IPInfo[] = [];
    const timestamp = new Date().toISOString();

    // Test direct connection to your APIs to see what IP they see from bolt.new
    const testEndpoints = [
      { 
        name: 'AI API', 
        url: '/api/health',
        description: 'This will show the IP that your AI API sees from bolt.new'
      },
    ];

    // Try to get bolt.new's infrastructure IP by making requests to your APIs
    for (const endpoint of testEndpoints) {
      try {
        console.log(`Testing connection to: ${endpoint.url}`);
        const response = await fetch(endpoint.url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        
        console.log(`Response from ${endpoint.name}:`, {
          status: response.status,
          headers: Object.fromEntries(response.headers.entries()),
          url: endpoint.url
        });
        
        // If successful, the server logs should show the real IP
        if (response.ok) {
          newIPs.push({
            ip: 'Check server logs',
            source: `${endpoint.name} (successful connection)`,
            timestamp
          });
        }
      } catch (error) {
        console.log(`Connection test to ${endpoint.name} failed:`, error);
        // Connection failed - this is expected if IPs aren't whitelisted yet
        newIPs.push({
          ip: 'Connection blocked - check server logs for IP',
          source: `${endpoint.name} (blocked - normal if not whitelisted)`,
          timestamp
        });
      }
    }
    
    // Add some known bolt.new IP ranges for reference
    // These are common infrastructure IPs that bolt.new might use
    const commonBoltIPs = [
      { ip: 'Check your server access logs', source: 'Server logs will show the real IP', timestamp },
      { ip: 'Look for recent connection attempts', source: 'Failed requests in your logs', timestamp }
    ];
    
    newIPs.push(...commonBoltIPs);

    setIpAddresses(newIPs);
    setLoading(false);
  };

  useEffect(() => {
    detectIPs();
  }, []);

  // Remove duplicates
  const uniqueIPs = ipAddresses.filter((ip, index, self) => 
    index === self.findIndex(t => t.ip === ip.ip)
  );

  return (
    <div className="space-y-6">
      {/* IP Detection Results */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-2 rounded-lg">
              <Globe className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-navy-900">Bolt.new IP Address Detection</h3>
              <p className="text-navy-600">These are the IP addresses your APIs need to whitelist</p>
            </div>
          </div>
          <Button
            onClick={detectIPs}
            loading={loading}
            variant="outline"
            size="sm"
            icon={RefreshCw}
          >
            {loading ? 'Detecting...' : 'Refresh'}
          </Button>
        </div>

        {uniqueIPs.length > 0 ? (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-800 mb-3">Connection Test Results:</h4>
              <div className="space-y-2">
                {uniqueIPs.map((ipInfo, index) => (
                  <div key={index} className="flex items-center justify-between bg-white p-3 rounded border">
                    <div className="flex items-center space-x-3">
                      <code className="bg-blue-100 px-3 py-1 rounded text-blue-800 font-mono text-sm">
                        {ipInfo.ip}
                      </code>
                      <span className="text-sm text-gray-600">from {ipInfo.source}</span>
                    </div>
                    {ipInfo.ip.match(/^\d+\.\d+\.\d+\.\d+$/) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(ipInfo.ip, ipInfo.ip)}
                        icon={Copy}
                        className="text-xs"
                      >
                        {copied === ipInfo.ip ? 'Copied!' : 'Copy'}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Instructions for finding the real IP */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-medium text-yellow-800 mb-2">🔍 How to Find Bolt.new's Real IP:</h4>
              <ol className="list-decimal list-inside space-y-2 text-sm text-yellow-700">
                <li><strong>Check your server access logs</strong> - Look for recent connection attempts to your APIs</li>
                <li><strong>Look for 403/blocked requests</strong> - These will show the source IP that bolt.new is using</li>
                <li><strong>Check firewall logs</strong> - Blocked connections will show the real source IP</li>
                <li><strong>Temporarily allow all IPs</strong> - Then check successful connection logs</li>
              </ol>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-medium text-green-800 mb-2">📋 Server Log Commands:</h4>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium text-green-700">Nginx/Apache logs:</span>
                  <code className="block bg-white p-2 rounded mt-1 text-xs">tail -f /var/log/nginx/access.log | grep "api\.windingriversoftware"</code>
                </div>
                <div>
                  <span className="font-medium text-green-700">FastAPI/Uvicorn logs:</span>
                  <code className="block bg-white p-2 rounded mt-1 text-xs">journalctl -f -u your-api-service</code>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-gray-400 mb-4">
              {loading ? 'Detecting IP addresses...' : 'No IP addresses detected'}
            </div>
            {!loading && (
              <Button onClick={detectIPs} variant="outline" size="sm">
                Try Again
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-navy-900 mb-4">Finding Bolt.new's Infrastructure IP</h3>
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="font-medium text-red-800 mb-2">⚠️ Important: IP Detection Issue</h4>
            <p className="text-sm text-red-700 mb-3">
              The IP detection services above show YOUR public IP, not bolt.new's infrastructure IP. 
              Your APIs need to whitelist bolt.new's actual server IPs.
            </p>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-800 mb-2">🔧 Method 1: Check Server Logs</h4>
            <ol className="list-decimal list-inside space-y-1 text-sm text-yellow-700">
              <li>Click "Refresh" above to trigger connection attempts from bolt.new</li>
              <li>Check your server access logs immediately after</li>
              <li>Look for connection attempts to your API endpoints</li>
              <li>The source IP in those logs is what you need to whitelist</li>
            </ol>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-medium text-green-800 mb-2">🔧 Method 2: Temporary Open Access</h4>
            <ol className="list-decimal list-inside space-y-1 text-sm text-green-700">
              <li>Temporarily allow all IPs (0.0.0.0/0) on your APIs</li>
              <li>Test the connection using the API Status tab</li>
              <li>Check successful connection logs for the source IP</li>
              <li>Whitelist that specific IP and remove the open access</li>
            </ol>
          </div>
          
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h4 className="font-medium text-purple-800 mb-2">🔧 CORS Configuration (Still Needed):</h4>
            <pre className="text-xs bg-white p-3 rounded border overflow-x-auto">
{`from fastapi.middleware.cors import CORSMiddleware

app.add_middleware(
    CORSMiddleware,
    allow_origins=[
        "https://bolt.new",
        "https://*.bolt.new",
        "https://stackblitz.com",
        # Add your production domains here
    ],
    allow_credentials=True,
    allow_methods=["*"],
    allow_headers=["*"],
)`}
            </pre>
          </div>
        </div>
      </div>

      {/* Additional Debug Info */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-navy-900 mb-4">Debug Information</h3>
        <div className="space-y-3 text-sm">
          <div>
            <span className="font-medium text-navy-700">Current Domain:</span>
            <code className="ml-2 bg-navy-100 px-2 py-1 rounded text-xs">
              {window.location.hostname}
            </code>
          </div>
          <div>
            <span className="font-medium text-navy-700">User Agent:</span>
            <code className="ml-2 bg-navy-100 px-2 py-1 rounded text-xs break-all">
              {navigator.userAgent}
            </code>
          </div>
          <div>
            <span className="font-medium text-navy-700">Configured APIs:</span>
            <div className="ml-2 mt-1 space-y-1">
              <div>
                <span className="text-xs text-navy-600">AI:</span>
                <code className="block bg-white p-2 rounded mt-1 text-xs">tail -f /var/log/nginx/access.log | grep "ai-api\.windingriversoftware"</code>
                <code className="ml-2 bg-navy-100 px-2 py-1 rounded text-xs">
                  {import.meta.env.VITE_AI_API_URL || 'Not configured'}
                </code>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};